import React, { useState, useEffect } from "react";
import axios from "axios";
import styles from "./PanelWriteOff.module.css";
import { API_URL } from '../../../config/api';


const PanelWriteOff = ({ warehouse, documentId, isOpen, onClose, refreshWriteoffs }) => {
  const [searchValue, setSearchValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [comment, setComment] = useState("");

  const token = localStorage.getItem("token"); // ✅ Берём токен

  // Загрузка из localStorage при открытии
  useEffect(() => {
    if (!isOpen) return;
    setSelectedProducts([]);
    setComment("");

    const savedProducts = localStorage.getItem("writeoff_selectedProducts");
    const savedComment = localStorage.getItem("writeoff_comment");

    if (savedProducts) setSelectedProducts(JSON.parse(savedProducts));
    if (savedComment) setComment(savedComment);

    if (!documentId || !warehouse) return;

    const fetchInitialData = async () => {
      if (!token) return console.error("❌ Нет токена!");
      try {
        const itemsRes = await axios.get(
          `${API_URL}/api/stock_changes/${documentId}/items`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const productsRes = await axios.get(
          `${API_URL}/api/products`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const items = itemsRes.data.data;
        const products = productsRes.data.data;

        const merged = items.map((item) => {
          const productInfo = products.find((p) => p.id === item.product_id);
          return {
            ...item,
            name_ru: productInfo?.name_ru || "Неизвестно",
            ntin: productInfo?.ntin || "",
            measure_unit_id: productInfo?.measure_unit_id || "",
            count: item.quantity,
            batches: [item],
          };
        });

        setSelectedProducts(merged);
      } catch (error) {
        console.error("Ошибка загрузки данных:", error);
      }
    };

    fetchInitialData();
  }, [isOpen, documentId, warehouse, token]);

  // Поиск товаров
  useEffect(() => {
    if (!warehouse || !token) return;
    if (searchValue.trim().length < 3) {
      setSuggestions([]);
      return;
    }

    const fetchSuggestions = async () => {
      try {
        const [byName, byNtin] = await Promise.all([
          axios.get(`${API_URL}/api/stocks`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { storage_id: warehouse.id, name_ru: searchValue },
          }),
          axios.get(`${API_URL}/api/stocks`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { storage_id: warehouse.id, ntin: searchValue },
          }),
        ]);

        const combined = [...byName.data.data, ...byNtin.data.data];
        const unique = combined.filter(
          (item, index, self) =>
            index === self.findIndex((t) => t.product_id === item.product_id)
        );

        setSuggestions(unique);
        setShowSuggestions(true);
      } catch (err) {
        console.error("Ошибка загрузки товаров:", err);
      }
    };

    const delayDebounce = setTimeout(fetchSuggestions, 400);
    return () => clearTimeout(delayDebounce);
  }, [searchValue, warehouse, token]);

  const handleSelect = async (item) => {
    setSearchValue("");
    setShowSuggestions(false);

    const exists = selectedProducts.some((p) => p.product_id === item.product_id);
    if (exists) return;

    try {
      const { data } = await axios.get(
        `${API_URL}/api/stock_change_items`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            storage_id: warehouse.id,
            product_id: item.product_id,
            is_used: false,
          },
        }
      );

      const batches = [...data.data].sort((a, b) => a.id - b.id);

      const newSelected = [...selectedProducts, { ...item, count: 1, batches }];
      setSelectedProducts(newSelected);
      localStorage.setItem("writeoff_selectedProducts", JSON.stringify(newSelected)); // ✅ сохраняем в localStorage
    } catch (err) {
      console.error("Ошибка загрузки партий:", err);
    }
  };

  const handleCountChange = (productId, value) => {
    const updated = selectedProducts.map((prod) => {
      if (prod.product_id === productId) {
        const maxQty = prod.batches.reduce((sum, b) => sum + b.quantity, 0);
        const count = Math.min(Math.max(1, Number(value)), maxQty);
        return { ...prod, count };
      }
      return prod;
    });
    setSelectedProducts(updated);
    localStorage.setItem("writeoff_selectedProducts", JSON.stringify(updated)); // ✅ обновляем localStorage
  };

  const calculatePrice = (product, count, field) => {
    if (!product.batches) return 0;

    let remaining = count;
    let total = 0;

    const batches = [...product.batches].sort((a, b) => a.id - b.id);

    for (let batch of batches) {
      if (remaining <= 0) break;

      const take = Math.min(batch.quantity, remaining);
      total += take * parseFloat(batch[field] || 0);
      remaining -= take;
    }

    return total;
  };

  const handleBack = () => {
    setSearchValue("");
    setSuggestions([]);
    setShowSuggestions(false);
    setSelectedProducts([]);
    setComment("");
    onClose();
  };

  const openFinishModal = () => {
    if (selectedProducts.length === 0) {
      alert("Добавьте хотя бы один товар для списания!");
      return;
    }
    setIsModalOpen(true);
  };

  const handleFinishWriteOff = async () => {
    if (!token) return console.error("❌ Нет токена!");

    try {
      const payload = {
        storage_id: warehouse.id,
        type: "writeoff",
        quantity: selectedProducts.reduce((sum, p) => sum + p.count, 0),
        status: "approved",
        aux: { comment },
        items: selectedProducts.map((p) => ({
          product_id: p.product_id,
          quantity: p.count,
          purchase_price: calculatePrice(p, p.count, "purchase_price") / p.count,
          sell_price: calculatePrice(p, p.count, "sell_price") / p.count,
          amount: calculatePrice(p, p.count, "purchase_price"),
        })),
      };

      await axios.post(`${API_URL}/api/stock_changes`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (typeof refreshWriteoffs === "function") refreshWriteoffs();
      setIsModalOpen(false);
      handleBack();
    } catch (err) {
      console.error("Ошибка при завершении списания:", err);
      alert("Ошибка при завершении списания");
    }
  };

  if (!warehouse) return null;

  return (
    <div className={styles.PanelWriteOff}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={handleBack}>
          <p className={styles.backText}>Списание</p>
        </button>
      </div>

      <div className={styles.content}>
        <div className={styles.firstBlock}>
          <p className={styles.text}>Склад</p>
          <h2 className={styles.title}>
            {warehouse.name} {warehouse.id}
          </h2>
          <button className={styles.change}>Изменить</button>
        </div>

        <div className={styles.secondBlock}>
          <p className={styles.title}>Итого количество списания</p>
          <p className={styles.price}>
            {selectedProducts.reduce((sum, p) => sum + p.count, 0)}
          </p>
        </div>

        <div className={styles.thirdBlock}>
          <p className={styles.title}>Итого списаний по цене закупки</p>
          <p className={styles.price}>
            {selectedProducts.reduce(
                (sum, p) => sum + calculatePrice(p, p.count, "purchase_price"),
                0
                )
                } ₸
            </p>
        </div>

        <div className={styles.fourthBlock}>
          <p className={styles.title}>Возможный доход</p>
          <p className={styles.price}>
            {selectedProducts.reduce(
                (sum, p) => sum + calculatePrice(p, p.count, "sell_price"),
                0
                )
            } ₸
            </p>
        </div>
      </div>

      <div className={styles.searchBlock}>
        <input
          type="search"
          name="search"
          className={styles.search}
          placeholder="Поиск товара по названию или штрихкоду"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onFocus={() => setShowSuggestions(true)}
        />

        {showSuggestions && suggestions.length > 0 && (
          <ul className={styles.suggestionList}>
            {suggestions.map((item) => (
              <li
                key={item.product_id}
                className={styles.suggestionItem}
                onClick={() => handleSelect(item)}
              >
                {item.name_ru} — {item.quantity} шт.
              </li>
            ))}
          </ul>
        )}
        <div className={styles.actions}>
            <button className={styles.addProduct}>Добавить товар</button>
            <button className={styles.endProduct} onClick={openFinishModal}>Завершить списание</button>
        </div>
      </div>

      {selectedProducts.length > 0 && (
        <table className={styles.productTable}>
          <thead>
            <tr>
              <th>№</th>
              <th>Название</th>
              <th>Штрихкод</th>
              <th>Остаток на складе</th>
              <th>Количество</th>
              <th>Ед. изм.</th>
              <th>Итого по закупке</th>
              <th>Итого по продаже</th>
            </tr>
          </thead>
          <tbody>
            {selectedProducts.map((p, index) => (
              <tr key={p.product_id}>
                <td>{index + 1}</td>
                <td>{p.name_ru}</td>
                <td>{p.ntin}</td>
                <td>{p.quantity}</td>
                <td>
                  <input
                    type="number"
                    value={p.count}
                    className={styles.changeNum}
                    min="1"
                    max={p.batches.reduce((sum, b) => sum + b.quantity, 0)}
                    onChange={(e) => handleCountChange(p.product_id, e.target.value)}
                />

                </td>
                <td>{p.measure_unit_id}</td>
                <td>{calculatePrice(p, p.count, "purchase_price")}</td>
                <td>{calculatePrice(p, p.count, "sell_price")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
       {isModalOpen && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <h3>Завершить списание</h3>
            <textarea
              placeholder="Комментарий"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className={styles.commentInput}
            />
            <div className={styles.modalActions}>
              <button onClick={() => setIsModalOpen(false)}>Отменить</button>
              <button onClick={handleFinishWriteOff}>Завершить</button>
            </div>
          </div>
        </div>
      )}
      
    </div>
  );
};

export default PanelWriteOff;
